/**
 * Plant & Equipment register entity DTOs (Business+).
 *
 * Mirrors Prisma `PlantAsset` + `ProjectPlantAssignment` +
 * `PlantAssetRequirement` + `PlantEvidence` + `PlantAssetEvent`
 * (prisma/schema.prisma, "Plant & Equipment Compliance Register" section).
 * Web parity with `app/api/plant-assets/*`.
 *
 * Compliance state is DERIVED server-side (lib/plant/compliance.ts) and
 * shipped as a result object — clients never recompute it. GREY means
 * "nothing asserted" (retired/archived or no requirements configured), and is
 * deliberately not a compliant state.
 */
import { z } from "zod";
import {
  CorrectiveActionStatusSchema,
  SiteInspectionKindSchema,
  SiteInspectionStatusSchema,
} from "./inspection";

export const PlantOwnershipTypeSchema = z.enum([
  "OWNED",
  "HIRED",
  "SUBCONTRACTOR",
  "CLIENT",
  "OTHER",
]);
export type PlantOwnershipType = z.infer<typeof PlantOwnershipTypeSchema>;

export const PlantOperationalStatusSchema = z.enum([
  "SERVICEABLE",
  "QUARANTINED",
  "OUT_OF_SERVICE",
  "UNDER_REPAIR",
  "RETIRED",
  "ARCHIVED",
]);
export type PlantOperationalStatus = z.infer<
  typeof PlantOperationalStatusSchema
>;

export const PlantProjectStatusSchema = z.enum([
  "PLANNED",
  "AWAITING_REVIEW",
  "APPROVED",
  "ON_SITE",
  "QUARANTINED",
  "REMOVED",
  "REJECTED",
  "CANCELLED",
]);
export type PlantProjectStatus = z.infer<typeof PlantProjectStatusSchema>;

export const PlantEvidenceTypeSchema = z.enum([
  "LOLER",
  "PUWER",
  "PAT",
  "SERVICE",
  "MAINTENANCE",
  "CALIBRATION",
  "INSURANCE",
  "USER_MANUAL",
  "DECLARATION_OF_CONFORMITY",
  "OTHER",
]);
export type PlantEvidenceType = z.infer<typeof PlantEvidenceTypeSchema>;

export const PlantEvidenceReviewStatusSchema = z.enum([
  "UPLOADED",
  "APPROVED",
  "REJECTED",
  "SUPERSEDED",
  "ARCHIVED",
]);
export type PlantEvidenceReviewStatus = z.infer<
  typeof PlantEvidenceReviewStatusSchema
>;

export const PlantRequirementKindSchema = z.enum([
  "EVIDENCE",
  "INSPECTION",
  "SERVICE",
  "MAINTENANCE",
]);
export type PlantRequirementKind = z.infer<typeof PlantRequirementKindSchema>;

export const PlantEvidenceValidityTypeSchema = z.enum([
  "DATE_RANGE",
  "NO_EXPIRY",
  "UNKNOWN",
]);
export type PlantEvidenceValidityType = z.infer<
  typeof PlantEvidenceValidityTypeSchema
>;

export const PlantEventTypeSchema = z.enum([
  "CREATED",
  "UPDATED",
  "IMPORTED",
  "ASSIGNED_TO_PROJECT",
  "MARKED_ON_SITE",
  "APPROVED_FOR_PROJECT",
  "REMOVED_FROM_PROJECT",
  "EVIDENCE_ADDED",
  "EVIDENCE_APPROVED",
  "EVIDENCE_REJECTED",
  "EVIDENCE_SUPERSEDED",
  "INSPECTION_COMPLETED",
  "DEFECT_REPORTED",
  "CORRECTIVE_ACTION_UPDATED",
  "QUARANTINED",
  "RETURNED_TO_SERVICE",
  "STATUS_CHANGED",
  "RETIRED",
  "ARCHIVED",
  "QR_ROTATED",
]);
export type PlantEventType = z.infer<typeof PlantEventTypeSchema>;

export const PlantComplianceStateSchema = z.enum([
  "GREEN",
  "AMBER",
  "RED",
  "GREY",
]);
export type PlantComplianceState = z.infer<typeof PlantComplianceStateSchema>;

export const PlantComplianceReasonSchema = z.object({
  code: z.string(),
  message: z.string(),
  requirementId: z.string().optional(),
  evidenceId: z.string().optional(),
  dueAt: z.string().optional(),
});
export type PlantComplianceReason = z.infer<typeof PlantComplianceReasonSchema>;
// #2411 server-boundary variant
export const PlantComplianceReasonStrict = PlantComplianceReasonSchema.strict();

export const PlantComplianceResultSchema = z.object({
  state: PlantComplianceStateSchema,
  label: z.string(),
  reasons: z.array(PlantComplianceReasonSchema),
  missingMandatoryCount: z.number().int(),
  expiredMandatoryCount: z.number().int(),
  expiringSoonCount: z.number().int(),
  overdueInspectionCount: z.number().int(),
  openCriticalActionCount: z.number().int(),
});
export type PlantComplianceResult = z.infer<typeof PlantComplianceResultSchema>;
// #2411 server-boundary variant
export const PlantComplianceResultStrict = PlantComplianceResultSchema.extend({
  reasons: z.array(PlantComplianceReasonStrict),
}).strict();

// ─── Response DTOs ───────────────────────────────────────────────────────────

export const PlantAssetRequirementSchema = z.object({
  id: z.string(),
  kind: PlantRequirementKindSchema,
  label: z.string(),
  evidenceType: PlantEvidenceTypeSchema.nullable(),
  mandatory: z.boolean(),
  warningDays: z.number().int(),
  recurrenceDays: z.number().int().nullable(),
  firstDueAt: z.string().datetime().nullable(),
  active: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type PlantAssetRequirement = z.infer<typeof PlantAssetRequirementSchema>;
// #2411 server-boundary variant
export const PlantAssetRequirementStrict = PlantAssetRequirementSchema.strict();

// Note: no raw file URLs on the wire — files are fetched via the presign-on-
// read endpoint (`/api/plant-assets/[assetId]/evidence/[evidenceId]/file`).
export const PlantEvidenceSchema = z.object({
  id: z.string(),
  requirementId: z.string().nullable(),
  type: PlantEvidenceTypeSchema,
  title: z.string(),
  reviewStatus: PlantEvidenceReviewStatusSchema,
  validityType: PlantEvidenceValidityTypeSchema,
  performedAt: z.string().datetime().nullable(),
  validFrom: z.string().datetime().nullable(),
  validUntil: z.string().datetime().nullable(),
  nextDueAt: z.string().datetime().nullable(),
  issuerName: z.string().nullable(),
  competentPersonName: z.string().nullable(),
  reference: z.string().nullable(),
  fileName: z.string().nullable(),
  contentType: z.string().nullable(),
  hasFile: z.boolean(),
  reviewNotes: z.string().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  supersedesEvidenceId: z.string().nullable(),
  supersededAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /**
   * The row's current version, stamped by every write (an edit or a review
   * decision). Clients echo it back as `expectedUpdatedAt` on approve/reject
   * so the server can refuse a decision made against content the reviewer
   * never saw (#2649). REQUIRED, so a build compiled before this field
   * existed fails its parse and degrades through `safeParseOrWarn` rather
   * than approving against a version it never saw.
   */
  updatedAt: z.string().datetime(),
});
export type PlantEvidence = z.infer<typeof PlantEvidenceSchema>;
// #2411 server-boundary variant
export const PlantEvidenceStrict = PlantEvidenceSchema.strict();

export const PlantAssetEventSchema = z.object({
  id: z.string(),
  type: PlantEventTypeSchema,
  eventAt: z.string().datetime(),
  summary: z.string(),
  projectId: z.string().nullable(),
  projectPlantAssignmentId: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type PlantAssetEvent = z.infer<typeof PlantAssetEventSchema>;
// #2411 server-boundary variant
export const PlantAssetEventStrict = PlantAssetEventSchema.strict();

export const ProjectPlantAssignmentSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  plantAssetId: z.string(),
  status: PlantProjectStatusSchema,
  plannedFrom: z.string().datetime().nullable(),
  arrivedAt: z.string().datetime().nullable(),
  approvedAt: z.string().datetime().nullable(),
  removedAt: z.string().datetime().nullable(),
  siteLocation: z.string().nullable(),
  ownershipTypeSnapshot: PlantOwnershipTypeSchema,
  suppliedBySnapshot: z.string().nullable(),
  ramsReference: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type ProjectPlantAssignment = z.infer<
  typeof ProjectPlantAssignmentSchema
>;
// #2411 server-boundary variant
export const ProjectPlantAssignmentStrict =
  ProjectPlantAssignmentSchema.strict();

export const PlantAssetSchema = z.object({
  id: z.string(),
  assetNumber: z.string(),
  name: z.string(),
  category: z.string(),
  equipmentType: z.string().nullable(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  ownershipType: PlantOwnershipTypeSchema,
  ownerName: z.string().nullable(),
  subcontractorId: z.string().nullable(),
  hireSupplier: z.string().nullable(),
  hireReference: z.string().nullable(),
  operationalStatus: PlantOperationalStatusSchema,
  photoUrl: z.string().nullable(),
  notes: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PlantAsset = z.infer<typeof PlantAssetSchema>;
// #2411 server-boundary variant
export const PlantAssetStrict = PlantAssetSchema.strict();

const PlantAssetCurrentProject = z.object({
  assignmentId: z.string(),
  projectId: z.string(),
  projectTitle: z.string(),
  status: PlantProjectStatusSchema,
  siteLocation: z.string().nullable(),
});

const PlantAssetNextAction = z.object({
  label: z.string(),
  dueAt: z.string().nullable(),
});

export const PlantAssetListItemSchema = z.object({
  id: z.string(),
  assetNumber: z.string(),
  name: z.string(),
  category: z.string(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  registrationNumber: z.string().nullable(),
  ownershipType: PlantOwnershipTypeSchema,
  ownerName: z.string().nullable(),
  operationalStatus: PlantOperationalStatusSchema,
  photoUrl: z.string().nullable(),
  compliance: PlantComplianceResultSchema,
  currentProject: PlantAssetCurrentProject.nullable(),
  nextAction: PlantAssetNextAction.nullable(),
});
export type PlantAssetListItem = z.infer<typeof PlantAssetListItemSchema>;
// #2411 server-boundary variant
export const PlantAssetListItemStrict = PlantAssetListItemSchema.extend({
  compliance: PlantComplianceResultStrict,
  currentProject: PlantAssetCurrentProject.strict().nullable(),
  nextAction: PlantAssetNextAction.strict().nullable(),
}).strict();

export const PlantRegisterSummarySchema = z.object({
  total: z.number().int(),
  green: z.number().int(),
  amber: z.number().int(),
  red: z.number().int(),
  grey: z.number().int(),
  quarantined: z.number().int(),
  currentlyOnSite: z.number().int(),
  unassigned: z.number().int(),
});
export type PlantRegisterSummary = z.infer<typeof PlantRegisterSummarySchema>;
// #2411 server-boundary variant
export const PlantRegisterSummaryStrict = PlantRegisterSummarySchema.strict();

export const PlantAssetListResponseSchema = z
  .object({
    assets: z.array(PlantAssetListItemSchema),
    nextCursor: z.string().nullable(),
    total: z.number().int(),
    summary: PlantRegisterSummarySchema,
  })
  .strict();
export type PlantAssetListResponse = z.infer<
  typeof PlantAssetListResponseSchema
>;
// Server-boundary variant: retain permissive client parsing while rejecting
// extra keys at every nested response object.
export const PlantAssetListResponseStrict = PlantAssetListResponseSchema.extend(
  {
    assets: z.array(PlantAssetListItemStrict),
    summary: PlantRegisterSummaryStrict,
  },
).strict();

export const PlantAssignmentResponseStrict = z
  .object({ assignment: ProjectPlantAssignmentStrict })
  .strict();

export const PlantAssetResponseStrict = z
  .object({ asset: PlantAssetStrict })
  .strict();

export const PlantRequirementListResponseStrict = z
  .object({ requirements: z.array(PlantAssetRequirementStrict) })
  .strict();
export const PlantRequirementResponseStrict = z
  .object({ requirement: PlantAssetRequirementStrict })
  .strict();
export const PlantEvidenceListResponseStrict = z
  .object({ evidence: z.array(PlantEvidenceStrict) })
  .strict();
export const PlantEvidenceResponseStrict = z
  .object({ evidence: PlantEvidenceStrict })
  .strict();

// ─── Project-scoped mobile reads ───────────────────────────────────────────

/**
 * The small inspection projection used by the native Plant list and detail.
 * Item/checklist contents stay on the inspections route; this is only enough
 * to identify the latest recorded check and open the later inspection flow.
 */
export const MobilePlantInspectionSummarySchema = z.object({
  id: z.string(),
  projectId: z.string(),
  projectPlantAssignmentId: z.string(),
  title: z.string(),
  kind: SiteInspectionKindSchema.exclude(["GENERAL"]),
  status: SiteInspectionStatusSchema,
  inspectedAt: z.string().datetime(),
  templateKey: z.string().nullable(),
});
export type MobilePlantInspectionSummary = z.infer<
  typeof MobilePlantInspectionSummarySchema
>;
// #2411 server-boundary variant
export const MobilePlantInspectionSummaryStrict =
  MobilePlantInspectionSummarySchema.strict();

export const MobileProjectPlantItemSchema = z.object({
  assignment: ProjectPlantAssignmentSchema,
  asset: PlantAssetSchema,
  compliance: PlantComplianceResultSchema,
  openDefectCount: z.number().int().nonnegative(),
  latestInspection: MobilePlantInspectionSummarySchema.nullable(),
});
export type MobileProjectPlantItem = z.infer<
  typeof MobileProjectPlantItemSchema
>;
// #2411 server-boundary variant
export const MobileProjectPlantItemStrict = MobileProjectPlantItemSchema.extend(
  {
    assignment: ProjectPlantAssignmentStrict,
    asset: PlantAssetStrict,
    compliance: PlantComplianceResultStrict,
    latestInspection: MobilePlantInspectionSummaryStrict.nullable(),
  },
).strict();

export const MobileProjectPlantSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  attentionCount: z.number().int().nonnegative(),
  onSiteCount: z.number().int().nonnegative(),
  plannedCount: z.number().int().nonnegative(),
  removedCount: z.number().int().nonnegative(),
});
export type MobileProjectPlantSummary = z.infer<
  typeof MobileProjectPlantSummarySchema
>;
// #2411 server-boundary variant
export const MobileProjectPlantSummaryStrict =
  MobileProjectPlantSummarySchema.strict();

export const MobileProjectPlantResponseSchema = z.object({
  items: z.array(MobileProjectPlantItemSchema),
  summary: MobileProjectPlantSummarySchema,
});
export type MobileProjectPlantResponse = z.infer<
  typeof MobileProjectPlantResponseSchema
>;
// #2411 server-boundary variant
const ProjectPlantOpenDefectActionStrict = z
  .object({
    id: z.string(),
    projectId: z.string(),
    projectPlantAssignmentId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: CorrectiveActionStatusSchema.extract([
      "OPEN",
      "IN_PROGRESS",
      "CANCELLED",
    ]),
    needsCancellationReason: z.boolean(),
    responsiblePerson: z.string().nullable(),
    safetyCritical: z.boolean(),
    dueAt: z.string().datetime().nullable(),
    photoUrls: z.array(z.string()),
    createdAt: z.string().datetime(),
  })
  .strict();

export const ProjectPlantListResponseStrict = z
  .object({
    items: z.array(
      z
        .object({
          assignment: ProjectPlantAssignmentStrict,
          asset: PlantAssetStrict,
          compliance: PlantComplianceResultStrict,
          openDefectActions: z.array(ProjectPlantOpenDefectActionStrict),
          latestInspection: MobilePlantInspectionSummaryStrict.nullable(),
        })
        .strict(),
    ),
    openCount: z.number().int().nonnegative(),
  })
  .strict();
const AccountPlantCurrentAssignmentStrict = z
  .object({
    id: z.string(),
    projectId: z.string(),
    projectTitle: z.string(),
    status: PlantProjectStatusSchema,
    siteLocation: z.string().nullable(),
  })
  .strict();
const PlantAssignmentHistoryStrict = ProjectPlantAssignmentSchema.extend({
  projectTitle: z.string(),
}).strict();
const PlantAccountDetailInspectionStrict = z
  .object({
    id: z.string(),
    projectId: z.string(),
    title: z.string(),
    kind: SiteInspectionKindSchema,
    status: SiteInspectionStatusSchema,
    inspectedAt: z.string().datetime(),
    templateKey: z.string().nullable(),
  })
  .strict();

export const PlantAssetWebDetailResponseStrict = z
  .object({
    asset: PlantAssetStrict,
    compliance: PlantComplianceResultStrict.nullable(),
    currentAssignment: AccountPlantCurrentAssignmentStrict.nullable(),
    requirements: z.array(PlantAssetRequirementStrict),
    evidence: z.array(PlantEvidenceStrict),
    assignments: z.array(PlantAssignmentHistoryStrict),
    events: z.array(PlantAssetEventStrict),
    openActions: z.array(ProjectPlantOpenDefectActionStrict),
    inspections: z.array(PlantAccountDetailInspectionStrict),
  })
  .strict();
export const MobileProjectPlantResponseStrict =
  MobileProjectPlantResponseSchema.extend({
    items: z.array(MobileProjectPlantItemStrict),
    summary: MobileProjectPlantSummaryStrict,
  }).strict();

export const MobilePlantDefectActionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  status: CorrectiveActionStatusSchema.extract([
    "OPEN",
    "IN_PROGRESS",
    "CANCELLED",
  ]),
  needsCancellationReason: z.boolean(),
  responsiblePerson: z.string().nullable(),
  safetyCritical: z.boolean(),
  dueAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type MobilePlantDefectAction = z.infer<
  typeof MobilePlantDefectActionSchema
>;
// #2411 server-boundary variant
export const MobilePlantDefectActionStrict =
  MobilePlantDefectActionSchema.strict();

const MobileProjectPlantDetailProject = z.object({
  id: z.string(),
  title: z.string(),
});

export const MobileProjectPlantDetailResponseSchema = z.object({
  project: MobileProjectPlantDetailProject,
  assignment: ProjectPlantAssignmentSchema,
  asset: PlantAssetSchema,
  compliance: PlantComplianceResultSchema,
  requirements: z.array(PlantAssetRequirementSchema),
  evidence: z.array(PlantEvidenceSchema),
  events: z.array(PlantAssetEventSchema),
  openDefectActions: z.array(MobilePlantDefectActionSchema),
  inspections: z.array(MobilePlantInspectionSummarySchema),
});
export type MobileProjectPlantDetailResponse = z.infer<
  typeof MobileProjectPlantDetailResponseSchema
>;
// #2411 server-boundary variant
export const MobileProjectPlantDetailResponseStrict =
  MobileProjectPlantDetailResponseSchema.extend({
    project: MobileProjectPlantDetailProject.strict(),
    assignment: ProjectPlantAssignmentStrict,
    asset: PlantAssetStrict,
    compliance: PlantComplianceResultStrict,
    requirements: z.array(PlantAssetRequirementStrict),
    evidence: z.array(PlantEvidenceStrict),
    events: z.array(PlantAssetEventStrict),
    openDefectActions: z.array(MobilePlantDefectActionStrict),
    inspections: z.array(MobilePlantInspectionSummaryStrict),
  }).strict();

// ─── Mobile QR resolution ──────────────────────────────────────────────────

/**
 * The bearer credential encoded by a Plant label is 32 random bytes rendered
 * as base64url (43 characters). Keep this contract deliberately narrower than
 * a generic URL: the native app extracts the token locally and sends it in a
 * POST body, so reverse proxies and analytics never receive it in a path.
 */
export const MobilePlantQrResolveRequestSchema = z
  .object({
    token: z
      .string()
      .regex(/^[A-Za-z0-9_-]{43}$/, "Invalid equipment QR token"),
  })
  .strict();
export type MobilePlantQrResolveRequest = z.infer<
  typeof MobilePlantQrResolveRequestSchema
>;

/**
 * Exact public-safe projection shared by every signed-in-but-not-authorised
 * scan. It intentionally omits ids, tenant/project detail, serials, suppliers,
 * notes, evidence, files and the bearer token itself.
 */
export const MobilePlantQrSafeAssetSchema = z.object({
  assetNumber: z.string(),
  name: z.string(),
  category: z.string(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  operationalStatus: PlantOperationalStatusSchema,
});
export type MobilePlantQrSafeAsset = z.infer<
  typeof MobilePlantQrSafeAssetSchema
>;
// #2411 server-boundary variant
export const MobilePlantQrSafeAssetStrict =
  MobilePlantQrSafeAssetSchema.strict();

const MobilePlantQrProjectDetailDestination = z.object({
  kind: z.literal("PROJECT_DETAIL"),
  projectId: z.string(),
  assignmentId: z.string(),
  plantAssetId: z.string(),
});

const MobilePlantQrAccountDetailDestination = z.object({
  kind: z.literal("ACCOUNT_DETAIL"),
  plantAssetId: z.string(),
});

const MobilePlantQrPublicOnlyDestination = z.object({
  kind: z.literal("PUBLIC_ONLY"),
});

export const MobilePlantQrDestinationSchema = z.discriminatedUnion("kind", [
  MobilePlantQrProjectDetailDestination,
  MobilePlantQrAccountDetailDestination,
  MobilePlantQrPublicOnlyDestination,
]);
export type MobilePlantQrDestination = z.infer<
  typeof MobilePlantQrDestinationSchema
>;
// #2411 server-boundary variant
export const MobilePlantQrDestinationStrict = z.discriminatedUnion("kind", [
  MobilePlantQrProjectDetailDestination.strict(),
  MobilePlantQrAccountDetailDestination.strict(),
  MobilePlantQrPublicOnlyDestination.strict(),
]);

export const MobilePlantQrResolveResponseSchema = z.object({
  asset: MobilePlantQrSafeAssetSchema,
  destination: MobilePlantQrDestinationSchema,
});
export type MobilePlantQrResolveResponse = z.infer<
  typeof MobilePlantQrResolveResponseSchema
>;
// #2411 server-boundary variant
export const MobilePlantQrResolveResponseStrict =
  MobilePlantQrResolveResponseSchema.extend({
    asset: MobilePlantQrSafeAssetStrict,
    destination: MobilePlantQrDestinationStrict,
  }).strict();

export const PlantInspectionTemplateItemSchema = z
  .object({
    prompt: z.string(),
    safetyCriticalByDefault: z.boolean().optional(),
  })
  .strict();
export type PlantInspectionTemplateItem = z.infer<
  typeof PlantInspectionTemplateItemSchema
>;

export const PlantInspectionTemplateSchema = z
  .object({
    key: z.string(),
    label: z.string(),
    suggestedFor: z.array(z.string()),
    items: z.array(PlantInspectionTemplateItemSchema),
  })
  .strict();
export type PlantInspectionTemplate = z.infer<
  typeof PlantInspectionTemplateSchema
>;

/** Shown wherever a built-in checklist is offered. Do not soften this wording. */
export const PLANT_INSPECTION_TEMPLATE_DISCLAIMER =
  "A starting point, not a complete check. Review and adapt it to this item, its manufacturer guidance, your risk assessment and your competent person's advice.";

const GENERIC_PLANT_PRE_USE: PlantInspectionTemplateItem[] = [
  { prompt: "Asset identity matches the register" },
  { prompt: "No visible structural damage", safetyCriticalByDefault: true },
  {
    prompt: "Guards and safety devices appear present",
    safetyCriticalByDefault: true,
  },
  { prompt: "Controls operate as expected", safetyCriticalByDefault: true },
  { prompt: "Tyres, tracks or wheels appear serviceable" },
  { prompt: "No visible leaks" },
  { prompt: "Warning devices operate" },
  { prompt: "Access and egress are clear" },
  { prompt: "Required evidence is current" },
  {
    prompt: "No unresolved safety-critical defect is shown",
    safetyCriticalByDefault: true,
  },
];

/** Built-in starter checklists shared by web, server validation and native. */
export const PLANT_INSPECTION_TEMPLATES: PlantInspectionTemplate[] = [
  {
    key: "plant-generic-pre-use",
    label: "General pre-use check",
    suggestedFor: ["Any"],
    items: GENERIC_PLANT_PRE_USE,
  },
  {
    key: "plant-weekly",
    label: "Weekly check",
    suggestedFor: ["Any"],
    items: [
      ...GENERIC_PLANT_PRE_USE,
      { prompt: "Fluid levels checked" },
      { prompt: "Greasing and lubrication carried out" },
      { prompt: "Service interval reviewed" },
      { prompt: "Damage since the last check recorded" },
    ],
  },
  {
    key: "lifting-equipment-pre-use",
    label: "Lifting equipment pre-use check",
    suggestedFor: ["Lifting", "Lifting accessory"],
    items: [
      { prompt: "Asset identity matches the register" },
      {
        prompt: "Current thorough examination evidence is recorded",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Safe working load is legible and correct",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "No damage, deformation or wear to load-bearing parts",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Hooks, latches and terminations are sound",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Wire rope, chain or webbing is undamaged",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Limit devices and overload protection operate",
        safetyCriticalByDefault: true,
      },
      { prompt: "Controls and emergency stop operate" },
    ],
  },
  {
    key: "mewp-pre-use",
    label: "MEWP pre-use check",
    suggestedFor: ["Access", "MEWP"],
    items: [
      { prompt: "Asset identity matches the register" },
      {
        prompt: "Current thorough examination evidence is recorded",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Ground conditions and outriggers suitable",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Guardrails, gate and harness points are sound",
        safetyCriticalByDefault: true,
      },
      { prompt: "Emergency lowering operates", safetyCriticalByDefault: true },
      {
        prompt: "Tilt and overload alarms operate",
        safetyCriticalByDefault: true,
      },
      { prompt: "Platform controls operate from both stations" },
      { prompt: "No hydraulic leaks" },
      { prompt: "Rescue plan is in place for this location" },
    ],
  },
  {
    key: "excavator-pre-use",
    label: "Excavator pre-use check",
    suggestedFor: ["Earthmoving", "Excavator"],
    items: [
      ...GENERIC_PLANT_PRE_USE,
      {
        prompt: "Quick hitch is correctly engaged and pinned",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Bucket and attachment pins secure",
        safetyCriticalByDefault: true,
      },
      { prompt: "Tracks and rollers serviceable" },
      { prompt: "Reversing and travel alarms operate" },
      { prompt: "Cab glass, mirrors and cameras clear" },
      {
        prompt: "Falling-object and rollover protection undamaged",
        safetyCriticalByDefault: true,
      },
    ],
  },
  {
    key: "telehandler-pre-use",
    label: "Telehandler pre-use check",
    suggestedFor: ["Lifting", "Telehandler"],
    items: [
      ...GENERIC_PLANT_PRE_USE,
      {
        prompt: "Load chart is present and legible",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Longitudinal stability indicator operates",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Forks and carriage undamaged and secure",
        safetyCriticalByDefault: true,
      },
      { prompt: "Boom and extension operate smoothly" },
      { prompt: "Reversing alarm and beacon operate" },
    ],
  },
  {
    key: "access-equipment-check",
    label: "Access equipment check",
    suggestedFor: ["Access", "Scaffold", "Ladder"],
    items: [
      { prompt: "Asset identity matches the register" },
      {
        prompt: "No damage, distortion or missing components",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Feet, stabilisers and locking devices sound",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Guardrails and toe boards fitted where required",
        safetyCriticalByDefault: true,
      },
      { prompt: "Inspection tag or record is current" },
      { prompt: "Assembled to the manufacturer's instructions" },
    ],
  },
  {
    key: "post-repair-return-check",
    label: "Post-repair return-to-service check",
    suggestedFor: ["Any"],
    items: [
      {
        prompt: "Reported defect has been rectified",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Repair carried out by a competent person",
        safetyCriticalByDefault: true,
      },
      { prompt: "Supporting evidence attached to the register" },
      {
        prompt: "Function tested through its full range",
        safetyCriticalByDefault: true,
      },
      {
        prompt: "Guards and safety devices refitted",
        safetyCriticalByDefault: true,
      },
      { prompt: "No new defect introduced by the repair" },
    ],
  },
];

export function plantInspectionTemplate(
  key: string,
): PlantInspectionTemplate | undefined {
  return PLANT_INSPECTION_TEMPLATES.find((template) => template.key === key);
}

export const PLANT_INSPECTION_TEMPLATE_KEYS = PLANT_INSPECTION_TEMPLATES.map(
  (template) => template.key,
);

// ─── Requests ────────────────────────────────────────────────────────────────

/**
 * A caller-supplied date. Validated HERE rather than at each route, because a
 * malformed-but-nonempty value ("not-a-date") otherwise passes every presence
 * check and is stored as null — a 201 that silently drops a compliance date
 * and leaves the record reading "validity unknown". Every plant date field
 * uses this, so the rejection is the same for web, mobile and API callers.
 */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})?$/;

/**
 * A real calendar date, checked by round-tripping the literal Y-M-D through
 * UTC. `Date.parse` NORMALISES rather than rejects — 2026-02-30 becomes
 * 2 March and 2026-02-29 becomes 1 March — so a caller could submit an
 * impossible date, get a 201, and have a silently different expiry stored.
 *
 * Only the written components are round-tripped, never the resolved instant:
 * a legitimate offset datetime like 2026-08-01T01:00:00+02:00 lands on
 * 31 July in UTC, and rejecting that would be wrong.
 */
function isRealCalendarDate(value: string): boolean {
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() + 1 === month &&
    utc.getUTCDate() === day
  );
}

const PlantDateInput = z.string().refine(
  (value) => {
    const dateOnly = ISO_DATE_ONLY.test(value);
    // Shape first: this is what rejects "1", "07/08/2026" and free text that
    // Date.parse would otherwise coerce into some arbitrary instant.
    if (!dateOnly && !ISO_DATE_TIME.test(value)) return false;
    if (!isRealCalendarDate(value)) return false;
    // Catches impossible time components (e.g. 25:00) that pass the shape.
    return !Number.isNaN(Date.parse(dateOnly ? `${value}T00:00:00Z` : value));
  },
  { message: "must be a valid ISO date (YYYY-MM-DD or an ISO date-time)" },
);

/**
 * A required free-text field. Trims FIRST, then enforces the minimum, so a
 * whitespace-only value is rejected rather than stored as a blank asset
 * number, requirement label or rejection reason. The routes' own `.trim()`
 * calls then operate on an already-trimmed value.
 */
const RequiredText = (max: number) => z.string().trim().min(1).max(max);

/**
 * A storage reference (URL or key) that is never the EMPTY STRING.
 *
 * "" is not a reference, but it is truthy-adjacent in all the wrong ways: it
 * survives `?? `, reads as "supplied" to an `!== undefined` check, and then
 * behaves as absent at the point of use — which let an empty `photoKey` mask a
 * document URL behind it past validation (#1986 round 17). Normalising it to
 * null here means no downstream code has to know the difference. Note "" maps
 * to NULL, not undefined: sending it means "clear this", not "leave it alone".
 */
const StorageRef = (max: number) =>
  z
    .string()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === "" ? null : value));

export const PlantAssetCreateRequest = z
  .object({
    assetNumber: RequiredText(60),
    name: RequiredText(160),
    category: RequiredText(80),
    equipmentType: z.string().max(80).nullable().optional(),
    make: z.string().max(80).nullable().optional(),
    model: z.string().max(80).nullable().optional(),
    serialNumber: z.string().max(120).nullable().optional(),
    registrationNumber: z.string().max(40).nullable().optional(),
    ownershipType: PlantOwnershipTypeSchema,
    ownerName: z.string().max(160).nullable().optional(),
    subcontractorId: z.string().nullable().optional(),
    hireSupplier: z.string().max(160).nullable().optional(),
    hireReference: z.string().max(120).nullable().optional(),
    photoUrl: StorageRef(2048),
    /** Storage key from the upload response — required for private photos so
     * the read path can presign (the raw private origin URL 403s). */
    photoKey: StorageRef(1024),
    notes: z.string().max(4000).nullable().optional(),
  })
  .strict();
export type PlantAssetCreateRequest = z.infer<typeof PlantAssetCreateRequest>;

// Deliberately excludes operationalStatus — status changes go through the
// explicit transition routes (archive/retire/quarantine/return-to-service)
// so audit and workflow checks cannot be bypassed.
export const PlantAssetUpdateRequest = z
  .object({
    assetNumber: RequiredText(60).optional(),
    name: RequiredText(160).optional(),
    category: RequiredText(80).optional(),
    equipmentType: z.string().max(80).nullable().optional(),
    make: z.string().max(80).nullable().optional(),
    model: z.string().max(80).nullable().optional(),
    serialNumber: z.string().max(120).nullable().optional(),
    registrationNumber: z.string().max(40).nullable().optional(),
    ownershipType: PlantOwnershipTypeSchema.optional(),
    ownerName: z.string().max(160).nullable().optional(),
    subcontractorId: z.string().nullable().optional(),
    hireSupplier: z.string().max(160).nullable().optional(),
    hireReference: z.string().max(120).nullable().optional(),
    photoUrl: StorageRef(2048),
    photoKey: StorageRef(1024),
    notes: z.string().max(4000).nullable().optional(),
  })
  .strict();
export type PlantAssetUpdateRequest = z.infer<typeof PlantAssetUpdateRequest>;

export const PlantRequirementCreateRequest = z
  .object({
    kind: PlantRequirementKindSchema,
    label: RequiredText(120),
    evidenceType: PlantEvidenceTypeSchema.nullable().optional(),
    mandatory: z.boolean().default(true),
    warningDays: z.number().int().min(0).max(365).default(30),
    recurrenceDays: z.number().int().min(1).max(3650).nullable().optional(),
    firstDueAt: PlantDateInput.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();
export type PlantRequirementCreateRequest = z.infer<
  typeof PlantRequirementCreateRequest
>;

export const PlantRequirementUpdateRequest = z
  .object({
    label: RequiredText(120).optional(),
    evidenceType: PlantEvidenceTypeSchema.nullable().optional(),
    mandatory: z.boolean().optional(),
    warningDays: z.number().int().min(0).max(365).optional(),
    recurrenceDays: z.number().int().min(1).max(3650).nullable().optional(),
    firstDueAt: PlantDateInput.nullable().optional(),
    active: z.boolean().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();
export type PlantRequirementUpdateRequest = z.infer<
  typeof PlantRequirementUpdateRequest
>;

export const PlantEvidenceCreateRequest = z
  .object({
    type: PlantEvidenceTypeSchema,
    title: RequiredText(160),
    requirementId: z.string().nullable().optional(),
    validityType: PlantEvidenceValidityTypeSchema.default("UNKNOWN"),
    performedAt: PlantDateInput.nullable().optional(),
    validFrom: PlantDateInput.nullable().optional(),
    validUntil: PlantDateInput.nullable().optional(),
    nextDueAt: PlantDateInput.nullable().optional(),
    issuerName: z.string().max(160).nullable().optional(),
    competentPersonName: z.string().max(160).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
    fileUrl: StorageRef(2048),
    fileKey: StorageRef(1024),
    fileName: z.string().max(255).nullable().optional(),
    contentType: z.string().max(120).nullable().optional(),
    /** When replacing: id of the evidence record this one supersedes. */
    supersedesEvidenceId: z.string().nullable().optional(),
  })
  .strict();
export type PlantEvidenceCreateRequest = z.infer<
  typeof PlantEvidenceCreateRequest
>;

export const PlantEvidenceUpdateRequest = z
  .object({
    title: RequiredText(160).optional(),
    requirementId: z.string().nullable().optional(),
    validityType: PlantEvidenceValidityTypeSchema.optional(),
    performedAt: PlantDateInput.nullable().optional(),
    validFrom: PlantDateInput.nullable().optional(),
    validUntil: PlantDateInput.nullable().optional(),
    nextDueAt: PlantDateInput.nullable().optional(),
    issuerName: z.string().max(160).nullable().optional(),
    competentPersonName: z.string().max(160).nullable().optional(),
    reference: z.string().max(120).nullable().optional(),
  })
  .strict();
export type PlantEvidenceUpdateRequest = z.infer<
  typeof PlantEvidenceUpdateRequest
>;

/**
 * Quarantine is a GLOBAL "do not use" on the item, not a per-project note —
 * it is the strongest statement the register can make, so it requires a stated
 * reason that goes into the item's history and onto its label.
 */
export const PlantQuarantineRequest = z
  .object({
    reason: RequiredText(500),
    /** Optional link to the corrective action that caused it. */
    correctiveActionId: z.string().nullable().optional(),
  })
  .strict();
export type PlantQuarantineRequest = z.infer<typeof PlantQuarantineRequest>;

/**
 * Returning to service is a competent-person assertion, so it names WHO
 * checked it and what they did — a bare status flip would leave no record of
 * why the item is safe again.
 */
export const PlantReturnToServiceRequest = z
  .object({
    notes: RequiredText(1000),
    checkedByName: RequiredText(160),
  })
  .strict();
export type PlantReturnToServiceRequest = z.infer<
  typeof PlantReturnToServiceRequest
>;

/** Out of service is a softer "not in use" than quarantine; reason optional. */
export const PlantOutOfServiceRequest = z
  .object({
    reason: z.string().max(500).nullable().optional(),
  })
  .strict();
export type PlantOutOfServiceRequest = z.infer<typeof PlantOutOfServiceRequest>;

/**
 * Approve a record awaiting review. `expectedUpdatedAt` is the CLIENT-SUPPLIED
 * expected version (#2649, If-Match-style): the loaded row's `updatedAt` from
 * `PlantEvidence`. OPTIONAL per the site-files precedent
 * (`SiteFileEditableFields`): absent means the server falls back to its own
 * fresh re-read's version — today's behaviour — so the web ship is decoupled
 * from the mobile release and a bodyless approve from an older build still
 * parses. Supplied, the decision loses to any write that moved the row after
 * the reviewer's screen loaded.
 */
export const PlantEvidenceApproveRequest = z
  .object({
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict();
export type PlantEvidenceApproveRequest = z.infer<
  typeof PlantEvidenceApproveRequest
>;

export const PlantEvidenceRejectRequest = z
  .object({
    reviewNotes: RequiredText(2000),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict();
export type PlantEvidenceRejectRequest = z.infer<
  typeof PlantEvidenceRejectRequest
>;

/* ------------------------------------------------------------------ *
 * Defect reporting (§9.8, §13.13)
 * ------------------------------------------------------------------ */

/**
 * Report a defect on a placed item.
 *
 * `safetyCritical` FORCES quarantine — the server computes
 * `safetyCritical || quarantine` and ignores a `quarantine: false` sent
 * alongside it. A defect serious enough to be marked safety-critical, on a
 * machine left in service, is the exact hole the register exists to close, so
 * the decision must not be a checkbox someone can untick in a hurry.
 */
export const PlantDefectCreateRequest = z
  .object({
    title: RequiredText(180),
    description: z
      .string()
      .trim()
      .max(2000)
      .nullable()
      .optional()
      .transform((value) => (value === "" ? null : value)),
    assignedToPortalUserId: z.string().trim().min(1).nullable().optional(),
    assignedToName: z
      .string()
      .trim()
      .max(160)
      .nullable()
      .optional()
      .transform((value) => (value === "" ? null : value)),
    dueAt: PlantDateInput.nullable().optional(),
    photoUrls: z.array(z.string().trim().min(1).max(2048)).max(10).default([]),
    safetyCritical: z.boolean().default(false),
    /**
     * Quarantine a NON-critical defect as well (spec §9.8.5). Ignored when
     * safetyCritical is true, where quarantine is mandatory rather than chosen.
     */
    quarantine: z.boolean().default(false),
  })
  .strict();
export type PlantDefectCreateRequest = z.infer<typeof PlantDefectCreateRequest>;

export const PlantDefectActionUpdateRequest = z
  .object({
    status: CorrectiveActionStatusSchema.extract(["DONE", "CANCELLED"]),
    reason: z
      .string()
      .trim()
      .max(2000)
      .nullable()
      .optional()
      .transform((value) => (value === "" ? null : value)),
  })
  .strict()
  .refine((value) => value.status !== "CANCELLED" || Boolean(value.reason), {
    message: "A reason is required when cancelling a corrective action",
    path: ["reason"],
  });
export type PlantDefectActionUpdateRequest = z.infer<
  typeof PlantDefectActionUpdateRequest
>;

/* ------------------------------------------------------------------ *
 * Project assignment
 * ------------------------------------------------------------------ */

/**
 * The assignment actions a user can take, and the ONLY producers of an
 * assignment status besides creation and the account-level quarantine.
 *
 * `QUARANTINED` is deliberately absent. Quarantine is a GLOBAL property of the
 * ASSET — one item, quarantined everywhere — and it is owned by
 * `/api/plant-assets/[assetId]/quarantine`, which flips the current placement
 * as a side effect. Letting a placement be quarantined directly would produce
 * an item quarantined on one site and serviceable on another, which is exactly
 * the state the register exists to make impossible. Likewise `PLANNED`: it is
 * the created state, not a transition.
 */
export const PlantAssignmentActionSchema = z.enum([
  "SUBMIT_FOR_REVIEW",
  "APPROVE",
  "REJECT",
  "MARK_ON_SITE",
  "REMOVE",
  "CANCEL",
]);
export type PlantAssignmentAction = z.infer<typeof PlantAssignmentActionSchema>;

/**
 * Place an asset on a project.
 *
 * Ownership is SNAPSHOT at assignment time (`ownershipTypeSnapshot`,
 * `suppliedBySnapshot` on the row) rather than joined live: a hire company can
 * change months later, and the audit trail must say who supplied the item to
 * THIS site at the time, not who supplies it today. The server takes those
 * from the asset — they are not client input.
 */
export const PlantAssignmentCreateRequest = z
  .object({
    plantAssetId: z.string().min(1),
    plannedFrom: PlantDateInput.nullable().optional(),
    siteLocation: z.string().max(200).nullable().optional(),
    ramsReference: z.string().max(200).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();
export type PlantAssignmentCreateRequest = z.infer<
  typeof PlantAssignmentCreateRequest
>;

/**
 * Advance an assignment, or edit its site detail.
 *
 * `action` and the detail fields are independent: a PATCH may carry either or
 * both. Absent means "leave alone" and null means "clear", the same contract
 * the asset PATCH uses.
 */
export const PlantAssignmentUpdateRequest = z
  .object({
    action: PlantAssignmentActionSchema.optional(),
    /** Required by REJECT and REMOVE — the audit trail needs the why. */
    reason: z.string().max(1000).nullable().optional(),
    plannedFrom: PlantDateInput.nullable().optional(),
    siteLocation: z.string().max(200).nullable().optional(),
    ramsReference: z.string().max(200).nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, {
    message: "Provide an action or at least one field to update",
  })
  .refine(
    (v) =>
      !(v.action === "REJECT" || v.action === "REMOVE") ||
      (v.reason ?? "").trim().length > 0,
    {
      message: "A reason is required when rejecting or removing",
      path: ["reason"],
    },
  );
export type PlantAssignmentUpdateRequest = z.infer<
  typeof PlantAssignmentUpdateRequest
>;
/* ------------------------------------------------------------------ *
 * Import (§20)
 * ------------------------------------------------------------------ */

export const PlantImportFieldSchema = z.enum([
  "assetNumber",
  "name",
  "category",
  "equipmentType",
  "make",
  "model",
  "serialNumber",
  "registrationNumber",
  "ownershipType",
  "ownerName",
  "hireSupplier",
  "hireReference",
  "operationalStatus",
  "validUntil",
  "notes",
]);
export type PlantImportField = z.infer<typeof PlantImportFieldSchema>;

/**
 * What happens to a row that matches an existing asset.
 *
 * There is deliberately no "overwrite everything" (§20.5): the destructive
 * option on a compliance register is the one a customer picks once, by
 * accident, and cannot undo.
 */
export const PlantImportDuplicateModeSchema = z.enum([
  "SKIP",
  "UPDATE_ON_ASSET_NUMBER",
]);
export type PlantImportDuplicateMode = z.infer<
  typeof PlantImportDuplicateModeSchema
>;

const PlantImportBase64 = z
  .string()
  .min(1)
  .max(13_981_016)
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, "File content must be valid base64")
  .refine((value) => value.length % 4 === 0, {
    message: "File content must be valid base64",
  });

const PlantImportMappingSchema = z
  .record(
    z
      .string()
      .regex(/^(0|[1-9]\d{0,4})$/, "Mapping keys must be column indexes"),
    PlantImportFieldSchema.nullable(),
  )
  .refine((mapping) => Object.keys(mapping).length <= 1_000, {
    message: "A maximum of 1,000 columns can be mapped",
  });

export const PlantImportPreviewRequest = z
  .object({
    /**
     * 10MB decoded is at most 13,981,016 base64 characters. Bounding the
     * encoded string here prevents allocating an arbitrarily large Buffer
     * before the route can enforce the decoded-byte cap.
     */
    fileBase64: PlantImportBase64,
    fileName: z
      .string()
      .trim()
      .min(1)
      .max(260)
      .refine((name) => /\.(csv|xlsx)$/i.test(name), {
        message: "File must be .csv or .xlsx",
      }),
    /** Explicit sheet choice for a multi-sheet workbook (§20.1). */
    sheetName: z.string().max(120).nullable().optional(),
    /** Present on the second preview call, after the customer maps columns. */
    mapping: PlantImportMappingSchema.optional(),
    duplicateMode: PlantImportDuplicateModeSchema.default("SKIP"),
    generateMissingAssetNumbers: z.boolean().default(false),
  })
  .strict();
export type PlantImportPreviewRequest = z.infer<
  typeof PlantImportPreviewRequest
>;

export const PlantImportCommitRequest = z
  .object({
    fileBase64: PlantImportBase64,
    fileName: z
      .string()
      .trim()
      .min(1)
      .max(260)
      .refine((name) => /\.(csv|xlsx)$/i.test(name), {
        message: "File must be .csv or .xlsx",
      }),
    sheetName: z.string().max(120).nullable().optional(),
    /** Confirmed column → field mapping, keyed by column index. */
    mapping: PlantImportMappingSchema,
    duplicateMode: PlantImportDuplicateModeSchema.default("SKIP"),
    /**
     * Generate `PL-0001`-style numbers for rows with none. Requires explicit
     * confirmation (§20.3) — never inferred from the file.
     */
    generateMissingAssetNumbers: z.boolean().default(false),
    /**
     * §20.5 idempotency is carried by the `Idempotency-Key` HEADER, handled by
     * the shared `withIdempotency` helper — not a body field, so the retry
     * semantics match every other idempotent route in the app.
     */
  })
  .strict();
export type PlantImportCommitRequest = z.infer<typeof PlantImportCommitRequest>;
